// Us / Me = the journal on her desk (D_Desk `jr`): one book, two pages, US / ME bookmarks on top. Real state and the
// same actions as the phone's Us and Me tabs. Pages the journal doesn't draw (a chapter, your week, VIP, the buddy page,
// test tools) open as the phone screen in a panel over the room.
import { CHAR_IDS, type AppState, type CharId, type CheckIn } from '@shared/types';
import { C } from '@shared/content/characters';
import { AI_NOTE, fill } from '@shared/content/lines';
import { CHAPTER, STAGE_CAP } from '@shared/content/story';
import { COMING_SOON_LABEL } from '@shared/rules';
import { XFace } from '../../components';
import { cardsAway, keepStops, lockedLabel, nextStop, plural } from '../us/route';
import { deskAsset, type DeskTime } from './media';
import { JOURNAL, PREFS, ROOM } from './roomCopy';
import s from './room.module.css';

export type DeskPrefs = Partial<Record<'stay' | 'notice' | 'wander' | 'meetings', boolean>>;

export interface JournalActions {
  chapter: (n: number) => void;
  locked: (text: string) => void;
  share: () => void;
  switchTo: (ch: CharId) => void;
  vip: () => void;
  memory: () => void;
  planTime: () => void;
  checkin: () => void;
  tz: () => void;
  pref: (key: 'stay' | 'notice' | 'meetings', on: boolean) => void;
  crisis: () => void;
  signOut: () => void;
  tools: () => void;
}

interface Props {
  st: AppState;
  view: 'us' | 'me';
  setView: (v: 'us' | 'me') => void;
  close: () => void;
  vw: number; vh: number;
  time: DeskTime;
  prefs: DeskPrefs | null;
  a: JournalActions;
}

const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
const t = (x: string, v: Record<string, string | number>) => x.replace(/\{(\w+)\}/g, (m, k) => (v[k] != null ? String(v[k]) : m));
const city = (tz: string) => (tz.split('/').pop() || tz).replace(/_/g, ' ');

export function RoomJournal({ st, view, setView, close, vw, vh, time, prefs, a }: Props) {
  const ch = st.ch, c = C[ch], R = ROOM[ch];
  const left = Math.round((vw - 1080) / 2), top = Math.max(24, Math.round((vh - 652) / 2) + 8);
  return (
    <div class={s.journalLayer} style={{ '--ink': R.ink } as Record<string, string>}>
      <button type="button" class={s.journalBack} aria-label={JOURNAL.close} tabIndex={-1} onClick={close} />
      <div class={s.book} role="dialog" aria-modal="true" aria-label={view === 'me' ? JOURNAL.me : t(JOURNAL.usAria, { n: c.name })} style={{ left, top }}>
        <div class={s.bookCover} aria-hidden="true" />
        <div class={s.bookPages} aria-hidden="true" />
        <div class={s.tabs}>
          {(['us', 'me'] as const).map((k) => (
            <button key={k} type="button" class={s.bookmark} aria-pressed={view === k ? 'true' : 'false'} onClick={() => setView(k)}>{k.toUpperCase()}</button>
          ))}
        </div>
        <button type="button" class={s.bookX} aria-label="Close" onClick={close}>
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round' }}><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        {view === 'us' ? <UsPages st={st} time={time} a={a} /> : <MePages st={st} prefs={prefs} a={a} />}
      </div>
    </div>
  );
}

function UsPages({ st, time, a }: { st: AppState; time: DeskTime; a: JournalActions }) {
  const ch = st.ch, c = C[ch], b = st.buddy, lv = st.level;
  const lit = st.streak.litToday;
  const next = nextStop(b);
  const away = cardsAway(lv);
  const stage = Math.max(1, Math.min(7, b.stage));
  const keeps = keepStops(b);
  const chapStop = (n: number) => b.route.find((r) => r.type === 'chap' && r.chap === n) || null;
  const w = st.week;
  const pct = Math.max(3, Math.round((100 * lv.into) / Math.max(1, lv.need)));
  const nextName = next && next.ready ? cap(next.label) : COMING_SOON_LABEL;
  return (
    <>
      <div class={s.page} style={{ left: 64 }}>
        <div>
          <h2 class={s.pageH}>{JOURNAL.us}</h2>
          <p class={s.pageSub}>{b.daysTogether > 0 ? t(JOURNAL.usSub, { n: c.name, k: b.daysTogether }) : t(JOURNAL.usSubNew, { n: c.name })}</p>
        </div>
        <div class={s.box}>
          <div class={s.streakRow}>
            <svg viewBox="0 0 24 24" width="38" height="38" aria-hidden="true" style={{ fill: lit ? 'var(--acc)' : 'none', stroke: lit ? 'var(--ink)' : '#8C857B', strokeWidth: 1.4, strokeLinejoin: 'round' }}>
              <path d="M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2.2 1.2-3.6 2.4-4.6.2 1.6 1 2.6 2.1 2.9C11 9 11.2 6 12 3z" />
            </svg>
            <div>
              <p class={s.serifBig}>{t(JOURNAL.streak, { k: st.streak.count })}</p>
              <p class={s.small}>{lit ? JOURNAL.lit : JOURNAL.notLit}</p>
            </div>
          </div>
          <div class={s.weekRow} aria-label="This week">
            {st.streak.week.map((d) => {
              const on = d.mark === 'lit' || d.mark === 'rest' || d.mark === 'freeze';
              const today = d.day === st.today;
              return (
                <span key={d.day} class={s.wkDay}>
                  <span style={{ color: today ? 'var(--ink)' : '#6B6254' }}>{d.label}</span>
                  <span class={s.wkDot} data-on={on ? 'true' : 'false'} data-today={today ? 'true' : 'false'} aria-label={`${d.label}: ${d.mark}`} role="img">
                    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" style={{ fill: 'none', stroke: on ? 'var(--on)' : 'transparent', strokeWidth: 3, strokeLinecap: 'round', strokeLinejoin: 'round' }}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                  </span>
                </span>
              );
            })}
          </div>
        </div>
        <div class={s.levelBlock}>
          <div class={s.levelRow}><p class={s.serifBig}>{t(JOURNAL.level, { k: lv.level })}</p><p class={s.smallBold}>{t(JOURNAL.points, { a: lv.into, b: lv.need })}</p></div>
          <div class={s.bar} role="progressbar" aria-label="Points to the next level" aria-valuemin={0} aria-valuemax={lv.need} aria-valuenow={lv.into}><div style={{ width: `${pct}%` }} /></div>
          {next ? (
            <>
              <p class={s.kicker}>{next.ready ? t(JOURNAL.next, { k: next.level }) : 'NEXT STOP'}</p>
              <p class={s.hand}>{nextName}</p>
              <p class={s.note2}>{t(JOURNAL.away, { k: Math.max(0, lv.need - lv.into), c: plural(away, 'card') })}</p>
            </>
          ) : <p class={s.note2}>{JOURNAL.allOpen}</p>}
        </div>
        <div class={s.weekFoot}>
          <div class={s.weekFootText}>
            <p class={s.kickerGrey}>{JOURNAL.week}</p>
            <p class={s.serifRow}>{w ? t(JOURNAL.weekLine, { c: plural(w.cards, 'card'), l: plural(w.lit, 'day') }) : '—'}</p>
          </div>
          <button type="button" class={s.outlineBtn} onClick={a.share}>{JOURNAL.share}</button>
        </div>
      </div>
      <div class={s.page} style={{ left: 604, gap: 12 }}>
        <div class={s.placeRow}>
          <div class={s.polaroid}><img src={deskAsset(ch, time, 'room')} alt={ROOM[ch].alt} /></div>
          <div class={s.placeText}>
            <p class={s.serifMid}>{c.placeName}</p>
            <p class={s.kicker}>{t(JOURNAL.stage, { k: stage })}</p>
            <p class={s.placeLine}>{STAGE_CAP[ch][stage - 1]}</p>
          </div>
        </div>
        <p class={s.kickerGrey} style={{ marginTop: 6 }}>{JOURNAL.story}</p>
        <div class={s.chapters}>
          {CHAPTER[ch].map((name, i) => {
            const n = i + 1;
            const open = n <= b.chaptersOpen;
            const stop = chapStop(n);
            const side = open ? (b.newChapter === n ? JOURNAL.fresh : JOURNAL.read) : stop && stop.ready ? `Level ${stop.level}` : COMING_SOON_LABEL;
            return (
              <button
                key={n} type="button" class={s.chap} data-open={open ? 'true' : 'false'} data-new={open && b.newChapter === n ? 'true' : 'false'}
                aria-label={`Chapter ${n}: ${name}. ${side}`}
                onClick={() => (open ? a.chapter(n) : a.locked(stop && stop.ready ? `Opens at level ${stop.level}` : COMING_SOON_LABEL))}
              >
                <span class={s.chapN}>{(n < 10 ? '0' : '') + n}</span>
                <span class={s.chapName}>{name}</span>
                <span class={s.chapSide}>{side}</span>
              </button>
            );
          })}
        </div>
        <p class={s.kickerGrey} style={{ marginTop: 4 }}>{t(JOURNAL.keeps, { k: b.keepsakes.length, t: keeps.length })}</p>
        <div class={s.keeps}>
          {keeps.map((r) => (
            <div key={r.idx} class={s.keep} data-open={r.reached ? 'true' : 'false'} title={r.reached ? r.label : undefined}>{r.reached ? r.label : lockedLabel(r)}</div>
          ))}
        </div>
      </div>
    </>
  );
}

const CHECKIN_LABEL: Record<CheckIn, string> = { light: 'Light', steady: 'Steady', close: 'Close' };

function MePages({ st, prefs, a }: { st: AppState; prefs: DeskPrefs | null; a: JournalActions }) {
  const ch = st.ch, c = C[ch], u = st.user;
  const mem = st.buddy.memory.slice(0, 3);
  const rows: { key: string; label: string; value: string; run: () => void; aria: string }[] = [
    { key: 'plan', label: JOURNAL.planAt, value: u.planTime, run: a.planTime, aria: `We plan at ${u.planTime}. Change` },
    { key: 'checkin', label: JOURNAL.checkins, value: CHECKIN_LABEL[u.checkin], run: a.checkin, aria: `Check-ins: ${CHECKIN_LABEL[u.checkin]}. Click to change.` },
    { key: 'tz', label: JOURNAL.tz, value: u.tzAuto ? t(JOURNAL.tzAuto, { c: city(u.tz) }) : city(u.tz), run: a.tz, aria: `Time zone: ${u.tzAuto ? 'automatic, ' : ''}${city(u.tz)}. Change` },
  ];
  return (
    <>
      <div class={s.page} style={{ left: 64, gap: 16 }}>
        <div>
          <h2 class={s.pageH}>{JOURNAL.me}</h2>
          <p class={s.pageSub}>{t(JOURNAL.meSub, { e: u.email, k: st.dayIndex, i: u.invite })}</p>
        </div>
        <div class={s.box}>
          <div class={s.buddyRow}>
            <XFace ch={ch} e={1} s={56} ring="var(--acc)" alt="" />
            <div class={s.buddyText}>
              <p class={s.kickerGrey}>{JOURNAL.buddy}</p>
              <p class={s.serifName}>{c.name}</p>
              <p class={s.placeLine}>{t(JOURNAL.buddyLine, { r: c.role, p: c.placeAt })}</p>
            </div>
          </div>
          <div class={s.switchRow}>
            <span class={s.kickerGrey} style={{ flex: 1 }}>{JOURNAL.switch}</span>
            {CHAR_IDS.map((k) => (
              <button key={k} type="button" class={s.switchBtn} aria-label={`Switch to ${C[k].name}`} aria-pressed={k === ch ? 'true' : 'false'} onClick={() => { if (k !== ch) a.switchTo(k); }}>
                <XFace ch={k} e={1} s={36} ring={k === ch ? C[k].acc : 'rgba(42,36,28,0.22)'} alt="" />
                <span>{C[k].name}</span>
              </button>
            ))}
          </div>
        </div>
        <button type="button" class={s.remembers} onClick={a.memory} aria-label={`What ${c.name} remembers. Change or delete`}>
          <span class={s.kickerGrey}>{t(JOURNAL.remembers, { N: c.name.toUpperCase() })}</span>
          {mem.length ? mem.map((m) => <span key={m.id} class={s.memLine}>{m.text}</span>) : <span class={s.placeLine}>{t(JOURNAL.rememberNone, { n: c.name })}</span>}
        </button>
        <div class={s.planBox}>
          <div><p class={s.serifRowBig}>{u.vip ? JOURNAL.vip : JOURNAL.free}</p><p class={s.smallBold}>{u.vip ? JOURNAL.vipSub : t(JOURNAL.freeSub, { k: st.usage.freeLimit })}</p></div>
          <button type="button" class={s.darkBtn} onClick={a.vip}>{u.vip ? JOURNAL.manage : JOURNAL.tryVip}</button>
        </div>
      </div>
      <div class={s.page} style={{ left: 604, gap: 6 }}>
        <p class={s.kickerGrey} style={{ margin: '6px 0 2px' }}>{JOURNAL.day}</p>
        <div class={s.rows}>
          {rows.map((r) => (
            <button key={r.key} type="button" class={s.row} aria-label={r.aria} onClick={r.run}><span>{r.label}</span><span class={s.rowValue}>{r.value}</span></button>
          ))}
        </div>
        {prefs && (
          <>
            <p class={s.kickerGrey} style={{ margin: '16px 0 2px' }}>{JOURNAL.computer}</p>
            <div class={s.rows}>
              {PREFS.map((p) => {
                const on = !!prefs[p.key];
                return (
                  <button key={p.key} type="button" class={s.prefRow} role="switch" aria-checked={on ? 'true' : 'false'} onClick={() => a.pref(p.key, !on)}>
                    <span class={s.prefText}><span class={s.prefLabel}>{p.label}</span><span class={s.prefSub}>{p.sub(c.pron)}</span></span>
                    <span class={s.switchTrack} data-on={on ? 'true' : 'false'} aria-hidden="true"><span /></span>
                  </button>
                );
              })}
            </div>
          </>
        )}
        <p class={s.disclaimer}>
          {fill(AI_NOTE, { n: c.name })}{' '}
          <button type="button" class={s.linkRed} onClick={a.crisis}>{JOURNAL.help}</button>
          {' · '}<button type="button" class={s.linkGrey} onClick={a.signOut}>{JOURNAL.signOut}</button>
          {' · '}<button type="button" class={s.linkGrey} onClick={a.tools}>{JOURNAL.tools}</button>
        </p>
      </div>
    </>
  );
}
